/**
 * Where the session lives, and who is allowed to lose it.
 *
 * The store is pluggable for one concrete reason: the platform admin panel must not
 * survive a closed tab, while a student on their own laptop should not be asked to
 * sign in every morning. Same client, different storage, no branching in call sites.
 */

export interface StoredSession {
  accessToken: string
  /** Null when the API declined to issue one (it is optional on `AuthResponse`). */
  refreshToken: string | null
  userId: string
  /** Unix seconds. Absent on older responses — treat as unknown, never as expired. */
  expiresAt?: number
}

export interface SessionStore {
  load(): StoredSession | null
  save(session: StoredSession): void
  clear(): void
}

const ACCESS_TOKEN_KEY = 'modulus.accessToken'
const REFRESH_TOKEN_KEY = 'modulus.refreshToken'
const USER_ID_KEY = 'modulus.userId'
const EXPIRES_AT_KEY = 'modulus.expiresAt'

/**
 * Browser storage, keyed identically whichever backing store is used, so switching
 * one app from local to session storage never strands keys in the other.
 *
 * Every access is wrapped: Safari in private mode throws on `localStorage.setItem`,
 * and a thrown setter during sign-in is a blank screen rather than a degraded one.
 */
function webStorageSession(storage: () => Storage): SessionStore {
  const read = (key: string): string | null => {
    try {
      return storage().getItem(key)
    } catch {
      return null
    }
  }

  const write = (key: string, value: string | null): void => {
    try {
      if (value === null) storage().removeItem(key)
      else storage().setItem(key, value)
    } catch {
      /* a session that cannot be persisted still works for this tab */
    }
  }

  return {
    load() {
      const accessToken = read(ACCESS_TOKEN_KEY)
      const userId = read(USER_ID_KEY)
      if (!accessToken || !userId) return null

      const expiresAt = read(EXPIRES_AT_KEY)
      return {
        accessToken,
        refreshToken: read(REFRESH_TOKEN_KEY),
        userId,
        ...(expiresAt ? { expiresAt: Number(expiresAt) } : {}),
      }
    },

    save(session) {
      write(ACCESS_TOKEN_KEY, session.accessToken)
      write(USER_ID_KEY, session.userId)
      write(REFRESH_TOKEN_KEY, session.refreshToken)
      write(EXPIRES_AT_KEY, session.expiresAt === undefined ? null : String(session.expiresAt))
    },

    clear() {
      write(ACCESS_TOKEN_KEY, null)
      write(USER_ID_KEY, null)
      write(REFRESH_TOKEN_KEY, null)
      write(EXPIRES_AT_KEY, null)
    },
  }
}

/** Survives a closed tab. The student app and the school dashboard. */
export const localStorageSession = (): SessionStore => webStorageSession(() => localStorage)

/** Dies with the tab. The platform admin panel. */
export const sessionStorageSession = (): SessionStore => webStorageSession(() => sessionStorage)

/** Nothing is persisted. Tests, and server-side rendering. */
export function memorySession(): SessionStore {
  let current: StoredSession | null = null
  return {
    load: () => current,
    save: (session) => {
      current = session
    },
    clear: () => {
      current = null
    },
  }
}
